"use client";
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, ButtonStyle, ButtonVariant, Card, CardTitle, TableTypes, DVIcon, Modal, PageTitle, Table, TableSlotType } from '@develos/next-components';
import { HistoryFilterType, PaginatedComponentDataTable, VehicleListElementType } from './types';
import { badgeVariant, isOwerweightText, isSensorMissed, mapToPlatform } from '@/helpers';
import { useFetch, usePageLocation, useTranslations } from '@develos/next-library';
import { useVehicleCategories } from '@/hooks';
import { emptyHistoryFilter } from './constants';
import { useSelector } from 'react-redux';
import { storeSelectors } from '@/store';
import VehicleListFilterContainer from './VehicleListFilterContainer';
import { VehicleDetails } from '@/components';
import { VehicleRecordDetailsType } from '@/types';
import { VehicleWeightLimitFilterMode } from '@/store/portalSettings';
import { useRouter, useSearchParams } from 'next/navigation';

const VehicleList = () => {
  const searchParams = useSearchParams();
  const { translate } = useTranslations();
  const { postMutation } = useFetch();
  const currentMetricLocalization = useSelector(storeSelectors.selectCurrentMetricLocalization);
  const weightLimitFilterMode = useSelector(storeSelectors.selectWeightLimitFilterMode);
  const [ showFilters, setShowFilters ] = useState<boolean>(false);
  const [ showModal, setShowModal ] = useState<boolean>(false);
  const [ filters , setFilters ] = useState<HistoryFilterType>({...emptyHistoryFilter});
  const { categories, isLoading: isLoadingCategories } = useVehicleCategories();
  const { data: traficListTable, mutate: loadVehicleData, isPending, abort } = postMutation<PaginatedComponentDataTable<VehicleListElementType>>("VehicleList/GetVehicles");
  const { data: activeVehicleRecordDetails, mutate: loadDetails, isPending: isDetailsLoading } = postMutation<VehicleRecordDetailsType>("VehicleList/GetVehicleById");

  const filterData = (predefinedFilters?: HistoryFilterType) => {
    abort();
    const requestFiters = {
      ...filters,
      ...predefinedFilters
    }

    if(currentMetricLocalization)
    {
      requestFiters.vehicleRecordsRequest!.metricLocalizationUid = currentMetricLocalization.uId;
      requestFiters.vehiclesOver35TMode = weightLimitFilterMode == VehicleWeightLimitFilterMode.Above35Ton ? true : false
    }
    loadVehicleData(requestFiters);
  }

  const handleRowAction = (vehicleRecordDetails: VehicleListElementType) => {
    setShowModal(true);
    loadDetails({ metricPointPlatformUId: vehicleRecordDetails.platformId, id: vehicleRecordDetails.id})
  }

  const onPageChange = (pageNumber: number, pageSize: number, sortDirection?: TableTypes.TableSortDirecton, sortColumn?: string) => {
    let newPageNumber = pageNumber;
    if (pageSize !== filters.paging.pageSize) {
      newPageNumber = 1;
    }
    const newFilters = {
      ...filters,
      paging: {
        ...filters.paging,
        pageNo: newPageNumber,
        pageSize,
        descending: sortDirection === TableTypes.TableSortDirecton.Desc,
        propertyName: sortColumn
      }
    };
    if (
      newFilters.paging.pageNo !== filters.paging.pageNo ||
      newFilters.paging.pageSize !== filters.paging.pageSize ||
      newFilters.paging.descending !== filters.paging.descending ||
      newFilters.paging.propertyName !== filters.paging.propertyName
    ) {
      setFilters(newFilters);
      filterData(newFilters);
    }
  };

  const onLoadDailyHistoryButtonClick = (lpnumber: string) => {
    setShowModal(false);
    const newFilters = {
      ...emptyHistoryFilter,
      above35LastDayPlateNumber: lpnumber,
        vehicleRecordsRequest: {
          ...filters.vehicleRecordsRequest,
          metricPointsUIds: currentMetricLocalization?.metricPointPlatforms.map(x => x.uId) ?? [],
        }
    };
    setFilters(newFilters);
    filterData(newFilters);
  };

  useEffect(() => {
    const queryParams = Object.fromEntries( searchParams.entries());
    if(Object.keys(queryParams).length && queryParams['above35LastDayPlateNumber']?.length > 0){
      setShowFilters(true);
      const params = {
        ...filters,
        above35LastDayPlateNumber: queryParams['above35LastDayPlateNumber'] ?? '',
        vehicleRecordsRequest: {
          ...filters.vehicleRecordsRequest,
          metricPointsUIds: currentMetricLocalization?.metricPointPlatforms.map(x => x.uId) ?? [],
        },
      };
      setFilters(params)
    }
  }, [searchParams])

  const slots: TableSlotType<VehicleListElementType & {class: string; actions: string}> = {
    class: (row: VehicleListElementType) => {
      return (categories ?? []).find(x => x.id === row.categoryCost)?.name ?? row.categoryCost;
    },
    isOverweight: (row: VehicleListElementType) => {
      return (
        <Badge variant={badgeVariant(row.isOverweight)}>
          {isOwerweightText(row.isOverweight)}
        </Badge>
      );
    },
    lane: (row: VehicleListElementType) => {
      return (
        <Badge variant={badgeVariant(row.lane === 0)}>
          {isSensorMissed(row.lane === 0)}
        </Badge>
      );
    },
    platformId: (row: VehicleListElementType) => {
      return mapToPlatform(row.platformId, currentMetricLocalization);
    },
    actions: (row: VehicleListElementType) => {
      return (
        <Button style={ButtonStyle.Clear} handleClick={() => handleRowAction(row)}>
          <DVIcon name="FilePenLine" className="w-4 h-4"/>
        </Button>
      );
    }
  };

  return (
    <div className='rm-relative rm-h-full rm-w-full'>
      <PageTitle 
        title={translate('Ruch drogowy | Historia ruchu pojazdów', "VehicleList")} 
        actions={[
          {
            style:ButtonStyle.Outlined,
            variant: ButtonVariant.Default,
            leftIcon: "SlidersHorizontal",
            handleClick:() => {setShowFilters(!showFilters)},
            children: "Filtry",
            size:"sm"
          }
        ]}
      />
      {showFilters && <VehicleListFilterContainer 
        isLoading={isPending} 
        filters={filters} 
        setFilters={(newFilters) => setFilters(
          {
            ...newFilters, 
            paging: {
              ...filters.paging, 
              pageNo: 1
            }
          }
        )} 
        onSubmit={() => filterData()} 
        /> 
      }
      <Card>
        <Card.Body>
          <CardTitle title='Wyniki wyszukiwania' />
          <Table
            data={traficListTable?.values ?? []}
            headers={traficListTable?.headers ?? []}
            options={traficListTable?.options}
            slots={slots}
            useServerPagination={true}
            currentPageNumber={filters.paging.pageNo}
            totalResults={traficListTable?.totalCount ?? 0}
            sortDirection={filters.paging.descending ? TableTypes.TableSortDirecton.Desc : TableTypes.TableSortDirecton.Asc}
            onPageChange={onPageChange}
            noData={{title: "Brak danych", description: "Dostosuj filtry lub ustaw je aby uzyskać żądane wyniki"}}
            isLoading={isPending || isLoadingCategories}
          />
        </Card.Body>
      </Card>
      <Modal
        header={translate('Szczegóły przejazdu', "VehicleList")}
        isOpen={showModal}
        onCancel={() => { setShowModal(false); }}
        buttons={[
          {
            variant: ButtonVariant.Default,
            children: "Zamknij",
            handleClick: () => { setShowModal(false); }
          },
          ...(activeVehicleRecordDetails && activeVehicleRecordDetails.weight >= 3500 ? [
            {
              variant: ButtonVariant.Default,
              style: ButtonStyle.Outlined,
              children: "Dzienna historia przejazdów",
              handleClick: () => { onLoadDailyHistoryButtonClick(activeVehicleRecordDetails.lpnumber) }
            }
          ] : [])
        ]}
        isLoading={isDetailsLoading}
      >
        <VehicleDetails details={activeVehicleRecordDetails}/>
      </Modal>
    </div>
  );
};

export default VehicleList;